import React, { useEffect, useRef } from 'react';
import styles from './ParticleNetwork.module.css';

// Dependency-free interactive particle network:
// - dots drift slowly and connect to nearby dots with faint lines
// - the cursor acts like an extra node: nearby dots link to it with a
//   brighter line and get gently pushed away, so the field visibly
//   reacts to movement instead of just looping in the background.
const ParticleNetwork = ({
  className,
  dotColor = '#d4af37', // brand gold
  lineColor = '212, 175, 55', // rgb of dotColor, used for rgba() lines
  cursorLineColor = '245, 223, 160', // brighter gold for cursor links
  linkDistance = 140,
  cursorRadius = 170,
  density = 7000, // px^2 per particle; lower = denser
  maxParticles = 220,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    const cursor = { x: null, y: null, active: false };

    const makeParticles = () => {
      const count = Math.min(
        maxParticles,
        Math.floor((width * height) / density)
      );
      particles = new Array(count).fill(0).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        // Constant base drift — never decays, so particles always keep
        // moving on their own regardless of cursor activity.
        bvx: (Math.random() - 0.5) * 0.55,
        bvy: (Math.random() - 0.5) * 0.55,
        // Cursor-push velocity — added on proximity, decays back to 0.
        pvx: 0,
        pvy: 0,
        r: 1.6 + Math.random() * 1.8,
      }));
    };

    const setup = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      makeParticles();
    };

    setup();

    if (prefersReducedMotion) {
      // Static, faint constellation — no motion, no cursor dependency.
      ctx.fillStyle = dotColor;
      particles.forEach((p) => {
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      return;
    }

    let rafId;
    let isVisible = true;

    const draw = () => {
      rafId = requestAnimationFrame(draw);
      if (!isVisible) return;

      ctx.clearRect(0, 0, width, height);

      // Move particles, bounce off edges, get pushed away from cursor.
      for (const p of particles) {
        if (cursor.active) {
          const dx = p.x - cursor.x;
          const dy = p.y - cursor.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < cursorRadius && dist > 0.001) {
            const force = (1 - dist / cursorRadius) * 0.6;
            p.pvx += (dx / dist) * force;
            p.pvy += (dy / dist) * force;
          }
        }

        p.x += p.bvx + p.pvx;
        p.y += p.bvy + p.pvy;

        // Only the cursor-push component decays — base drift is constant,
        // so the particle field is always alive, cursor or not.
        p.pvx *= 0.94;
        p.pvy *= 0.94;

        if (p.x < 0 || p.x > width) p.bvx *= -1;
        if (p.y < 0 || p.y > height) p.bvy *= -1;
        p.x = Math.max(0, Math.min(width, p.x));
        p.y = Math.max(0, Math.min(height, p.y));
      }

      // Connect nearby particles to each other.
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < linkDistance) {
            ctx.strokeStyle = `rgba(${lineColor}, ${(1 - dist / linkDistance) * 0.45})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Connect particles to the cursor itself, brighter, so the
      // field visibly "notices" where you are.
      if (cursor.active) {
        for (const p of particles) {
          const dx = p.x - cursor.x;
          const dy = p.y - cursor.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < cursorRadius) {
            ctx.strokeStyle = `rgba(${cursorLineColor}, ${(1 - dist / cursorRadius) * 0.7})`;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(cursor.x, cursor.y);
            ctx.stroke();
          }
        }
      }

      // Draw the dots on top of the lines.
      ctx.fillStyle = dotColor;
      for (const p of particles) {
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    rafId = requestAnimationFrame(draw);

    // Pointer-events on the canvas are disabled (so it never blocks
    // clicks on real content), so we track cursor position on window
    // instead — the move/touch events still bubble up through it.
    const handleMouseMove = (e) => {
      cursor.x = e.clientX;
      cursor.y = e.clientY;
      cursor.active = true;
    };
    const handleMouseLeave = () => {
      cursor.active = false;
    };
    const handleTouchMove = (e) => {
      if (e.touches && e.touches.length > 0) {
        cursor.x = e.touches[0].clientX;
        cursor.y = e.touches[0].clientY;
        cursor.active = true;
      }
    };
    const handleTouchEnd = () => {
      cursor.active = false;
    };
    const handleResize = () => setup();
    const handleVisibility = () => {
      isVisible = document.visibilityState === 'visible';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);
    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [
    dotColor,
    lineColor,
    cursorLineColor,
    linkDistance,
    cursorRadius,
    density,
    maxParticles,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className={className ? `${styles.particleCanvas} ${className}` : styles.particleCanvas}
      aria-hidden="true"
    />
  );
};

export default ParticleNetwork;
